import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { COMPANY } from "@/config/company";

/**
 * Impaginazione comune dei documenti legali (privacy, cookie, termini, IA).
 * Volutamente sobria e senza dipendenze: questi testi devono restare leggibili
 * e stampabili, ed essere raggiungibili senza JavaScript pesante.
 */
export const LegalLayout = ({ title, subtitle, updatedAt, children }) => {
  useEffect(() => {
    document.title = `${title} — ${COMPANY.brand}`;
  }, [title]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#1A1A1A]">
      <header className="border-b border-black/5 bg-white/80 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="font-serif text-2xl tracking-tight">
            {COMPANY.brand}
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#737373] hover:text-[#1A1A1A] transition-colors"
          >
            <ArrowLeft size={14} /> Torna al sito
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14 md:py-20">
        <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight leading-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-4 text-[#4A4A4A] font-light leading-relaxed">{subtitle}</p>
        )}
        <p className="mt-6 text-xs tracking-[0.2em] uppercase text-[#A3A3A3]">
          Ultimo aggiornamento: {updatedAt || COMPANY.legalUpdatedAt} · Versione{" "}
          {COMPANY.legalVersion}
        </p>

        <div className="mt-12 legal-prose">{children}</div>
      </main>

      <footer className="border-t border-black/5 py-10">
        <div className="max-w-3xl mx-auto px-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#737373]">
          <Link to="/privacy" className="hover:text-[#1A1A1A]">Privacy Policy</Link>
          <Link to="/cookie-policy" className="hover:text-[#1A1A1A]">Cookie Policy</Link>
          <Link to="/termini" className="hover:text-[#1A1A1A]">Termini di servizio</Link>
          <Link to="/intelligenza-artificiale" className="hover:text-[#1A1A1A]">Uso dell'IA</Link>
          <Link to="/note-legali" className="hover:text-[#1A1A1A]">Note legali</Link>
        </div>
      </footer>
    </div>
  );
};

/** Sezione numerata con titolo. */
export const Section = ({ n, title, children }) => (
  <section className="mt-11 first:mt-0">
    <h2 className="font-serif text-2xl tracking-tight text-[#1A1A1A]">
      {n != null && <span className="text-[#A78BFA] mr-2">{n}.</span>}
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-[15px] text-[#4A4A4A] font-light leading-[1.75]">
      {children}
    </div>
  </section>
);

/** Tabella responsive: su schermi stretti scorre orizzontalmente. */
export const Table = ({ head, rows }) => (
  <div className="overflow-x-auto -mx-2 px-2">
    <table className="w-full text-sm border-collapse min-w-[34rem]">
      <thead>
        <tr className="text-left">
          {head.map((h) => (
            <th
              key={h}
              className="border-b border-black/10 pb-2 pr-4 text-[11px] uppercase tracking-[0.15em] text-[#737373] font-normal align-bottom"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="align-top">
            {r.map((cell, j) => (
              <td
                key={j}
                className="border-b border-black/5 py-3 pr-4 text-[#4A4A4A] font-light leading-relaxed"
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Elenco puntato coerente con la tipografia dei documenti. */
export const List = ({ items }) => (
  <ul className="space-y-2 pl-5 list-disc marker:text-[#A78BFA]">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);
